import React, { useState } from "react";

import Form from "ui/Form";
import Input from "ui/Input";
import Typography from "ui/Typography";

// TODO: introduce 1 mutation that do both?
import { useAcceptTosMutation } from "./acceptTos.generated";
import { useEditNameMutation } from "components/ProfileCard/editName.generated";

import Alert from "ui/Alert";
import { Formik } from "formik";
import FormError from "ui/FormError";
import * as yup from "yup";
import Button from "ui/Button";
import styled from "styled-components/macro";
import ProfileImage from "components/ProfileImage";
import { SurfaceType } from "utils/analytics/analyticsConstants";

interface Props {
  studentId: string;
  onShortNameChange: (displayName: string) => void;
  onFinish: (displayName: string) => void;
}

const $FormContainer = styled.div`
  max-width: 25rem;
`;

const $Form = styled(Form)`
  width: 100%;
  margin-bottom: 2rem;
  text-align: left;
`;

const $FormMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const $ProfileImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem 0 1rem;
`;

const STUDENT_BASIC_INFORMATION_SCHEMA = yup.object().shape({
  shortName: yup
    .string()
    .required("Please enter a display name.")
    .max(40, "40 character limit."),
});

export default function BasicInformationForm({
  studentId,
  onShortNameChange,
  onFinish,
}: Props) {
  const [shortName, setShortName] = useState("");

  const [sendAcceptTos, { error: acceptTosError, loading: loadingAcceptTos }] =
    useAcceptTosMutation();

  const [sendEditName, { error: editNameError, loading: loadingEditName }] =
    useEditNameMutation();

  interface FormInput {
    shortName: string;
  }

  async function onClickNext({ shortName }: FormInput) {
    await sendEditName({
      variables: {
        userId: studentId,
        name: shortName,
      },
    });

    await sendAcceptTos();

    onFinish(shortName);
  }

  return (
    <$FormContainer data-testid="studentTosForm">
      <Typography size="h2" textAlign="center" fontWeight="normal">
        Let your teachers know it’s you!
      </Typography>
      <$ProfileImageContainer>
        <ProfileImage
          user={{ id: studentId, name: shortName }}
          analyticsSurface={SurfaceType.STUDENT_ONBOARDING_PAGE}
          isInitialImageUploader
        />
      </$ProfileImageContainer>

      {(acceptTosError || editNameError) && (
        // TODO: improve the error message
        <Alert variant="danger">
          Sorry, something went wrong. Please try again later.
        </Alert>
      )}

      <Formik
        initialValues={{
          shortName,
        }}
        onSubmit={onClickNext}
        validationSchema={STUDENT_BASIC_INFORMATION_SCHEMA}
        handleChange
        handleBlur
        validateOnChange={true}
      >
        {({ handleSubmit, handleBlur, handleChange, errors, values }) => (
          <$Form>
            <Form.Group controlId="formShortName">
              <Form.Label>
                <Typography size="subheading">Display name</Typography>
              </Form.Label>
              <Input
                type="text"
                name="shortName"
                value={values.shortName}
                placeholder="Your first name"
                onChange={(e) => {
                  setShortName(e.target.value);
                  onShortNameChange(e.target.value);
                  handleChange(e);
                }}
                onBlur={handleBlur}
                required
                aria-required
                aria-invalid={!!errors.shortName}
                aria-describedby="shortNameError"
              />
              <$FormMessageContainer>
                <FormError id="shortNameError" isInvalid={!!errors.shortName}>
                  {errors.shortName}
                </FormError>
              </$FormMessageContainer>
            </Form.Group>

            <Button
              block
              disabled={!values.shortName || !!errors.shortName}
              analyticsName="clicked_finish_student_onboarding"
              variant="primary"
              onClick={() => handleSubmit()}
              type="submit"
              requestInProgress={loadingAcceptTos || loadingEditName}
            >
              Let&apos;s go!
            </Button>
          </$Form>
        )}
      </Formik>
    </$FormContainer>
  );
}
